import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const [status, setStatus] = useState("polling"); // polling | success | failed | timeout
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const maxAttempts = 6;
    let timer;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        attempts++;
        if (attempts >= maxAttempts) {
          setStatus("timeout");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        setStatus("failed");
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="booking-success-page" className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Card className="p-10 rounded-3xl border border-stone-200 bg-white text-center">
          {status === "polling" && (
            <>
              <Loader2 className="w-14 h-14 text-pink-500 animate-spin mx-auto" />
              <h1 className="mt-6 font-heading text-3xl font-black tracking-tighter text-stone-900">Confirming your payment…</h1>
              <p className="text-stone-600 mt-2">This should only take a moment.</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
              <h1 className="mt-6 font-heading text-4xl font-black tracking-tighter text-stone-900">Payment successful!</h1>
              <p className="text-stone-600 mt-3">Your pickup is confirmed. We&rsquo;ll be at your door on schedule.</p>
              <div className="mt-8 flex gap-3 justify-center">
                {bookingId && (
                  <Link to={`/booking/${bookingId}`}>
                    <Button data-testid="view-booking-btn" className="rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold h-12 px-6">View booking</Button>
                  </Link>
                )}
                <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full h-12 px-6">Dashboard</Button>
              </div>
            </>
          )}
          {(status === "failed" || status === "timeout") && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto" />
              <h1 className="mt-6 font-heading text-3xl font-black tracking-tighter text-stone-900">
                {status === "timeout" ? "Still processing" : "Payment issue"}
              </h1>
              <p className="text-stone-600 mt-3">
                {status === "timeout"
                  ? "Your payment is taking longer than expected. Check your dashboard shortly."
                  : "We couldn't confirm your payment. Please try again or contact support."}
              </p>
              <div className="mt-8 flex gap-3 justify-center">
                {bookingId && (
                  <Link to={`/booking/${bookingId}`}>
                    <Button className="rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold h-12 px-6">Back to booking</Button>
                  </Link>
                )}
                <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full h-12 px-6">Dashboard</Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
